/**
 * Logging utility for dev-tools
 * Provides structured logging with environment-aware behavior
 */

export enum LogLevel {
  DEBUG = 0,
  INFO = 1,
  WARN = 2,
  ERROR = 3,
}

type LogContext = Record<string, unknown> | string | number | boolean | null;

class DevToolsLogger {
  private readonly isDevelopment = process.env.NODE_ENV === "development";
  private readonly minLevel: LogLevel;

  constructor() {
    // In development, log everything. In production, only warnings and errors
    this.minLevel = this.isDevelopment ? LogLevel.DEBUG : LogLevel.WARN;
  }

  private shouldLog(level: LogLevel): boolean {
    return level >= this.minLevel;
  }

  private formatMessage(
    level: string,
    message: string,
    context?: LogContext
  ): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [DEV-TOOLS] [${level}]`;

    if (context) {
      return `${prefix} ${message}`;
    }
    return `${prefix} ${message}`;
  }

  debug(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.DEBUG)) {
      // eslint-disable-next-line no-console -- Logger utility needs console access
      console.log(this.formatMessage("DEBUG", message), context || "");
    }
  }

  info(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.INFO)) {
      // eslint-disable-next-line no-console -- Logger utility needs console access
      console.info(this.formatMessage("INFO", message), context || "");
    }
  }

  warn(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.WARN)) {
      // eslint-disable-next-line no-console -- Logger utility needs console access
      console.warn(this.formatMessage("WARN", message), context || "");
    }
  }

  error(message: string, context?: LogContext): void {
    if (this.shouldLog(LogLevel.ERROR)) {
      // eslint-disable-next-line no-console -- Logger utility needs console access
      console.error(this.formatMessage("ERROR", message), context || "");
    }
  }

  /**
   * Log schema extraction related messages
   */
  schemaDebug(message: string, context?: LogContext): void {
    this.debug(`[SCHEMA] ${message}`, context);
  }

  /**
   * Log method execution related messages
   */
  methodDebug(message: string, context?: LogContext): void {
    this.debug(`[METHOD] ${message}`, context);
  }

  /**
   * Log form validation related messages
   */
  formDebug(message: string, context?: LogContext): void {
    this.debug(`[FORM] ${message}`, context);
  }

  /**
   * Log authentication related messages
   */
  authDebug(message: string, context?: LogContext): void {
    this.debug(`[AUTH] ${message}`, context);
  }
}

// Export singleton instance
export const logger = new DevToolsLogger();
